<template>
  <div class="family-tree">
    <div class="tree-heading">{{ $t('calculator.familyTree') }}</div>

    <!-- Empty state: just the deceased -->
    <div v-if="!hasAnyHeirs" class="tree-empty">
      <TreeNodePill :node="deceasedNode" :is-deceased="true" />
      <p class="tree-hint">{{ $t('calculator.addHeirsHint') }}</p>
    </div>

    <!-- Tree -->
    <div v-else class="tree-canvas">

      <!-- ══════ GRANDPARENTS ══════ -->
      <template v-if="grandparentNodes.length">
        <div class="gen-label">{{ $t('heir.group.grandparents') }}</div>
        <div class="fan-nodes" :class="{ single: grandparentNodes.length === 1 }">
          <div class="fan-node" v-for="n in grandparentNodes" :key="n.key">
            <TreeNodePill :node="n" :share="getPerPersonShare(n)" :blocked="isBlocked(n)" />
          </div>
        </div>
        <div class="v-stem" />
      </template>

      <!-- ══════ PARENTS ══════ -->
      <template v-if="parentNodes.length">
        <div class="gen-label">{{ $t('calculator.parents') }}</div>
        <div class="fan-nodes" :class="{ single: parentNodes.length === 1 }">
          <div class="fan-node" v-for="n in parentNodes" :key="n.key">
            <TreeNodePill :node="n" :share="getPerPersonShare(n)" :blocked="isBlocked(n)" />
          </div>
        </div>
        <div class="v-stem" />
      </template>

      <!-- ══════ CENTER: Siblings + DECEASED + Spouses ══════ -->
      <div class="center-row">
        <!-- Siblings (left side) -->
        <template v-if="siblingNodes.length">
          <div class="side-group">
            <div class="side-member" v-for="n in siblingNodes" :key="n.key">
              <TreeNodePill :node="n" :share="getPerPersonShare(n)" :blocked="isBlocked(n)" />
            </div>
          </div>
          <div class="h-connector" />
        </template>

        <!-- Deceased -->
        <TreeNodePill :node="deceasedNode" :is-deceased="true" />

        <!-- Spouses (right side) -->
        <template v-if="spouseNodes.length">
          <div class="marriage-connector" />
          <div class="side-group">
            <div class="side-member" v-for="n in spouseNodes" :key="n.key">
              <TreeNodePill :node="n" :share="getPerPersonShare(n)" :blocked="isBlocked(n)" />
            </div>
          </div>
        </template>
      </div>

      <!-- ══════ CHILDREN ══════ -->
      <template v-if="childNodes.length">
        <div class="v-stem" />
        <div class="gen-label">{{ $t('calculator.children') }}</div>
        <div class="fan-nodes" :class="{ single: childNodes.length === 1 }">
          <div class="fan-node" v-for="n in childNodes" :key="n.key">
            <TreeNodePill :node="n" :share="getPerPersonShare(n)" :blocked="isBlocked(n)" />
          </div>
        </div>
      </template>

      <!-- ══════ GRANDCHILDREN ══════ -->
      <template v-if="grandchildNodes.length">
        <div class="v-stem" />
        <div class="gen-label">{{ $t('heir.group.grandchildren') }}</div>
        <div class="fan-nodes" :class="{ single: grandchildNodes.length === 1 }">
          <div class="fan-node" v-for="n in grandchildNodes" :key="n.key">
            <TreeNodePill :node="n" :share="getPerPersonShare(n)" :blocked="isBlocked(n)" />
          </div>
        </div>
      </template>

      <!-- ══════ EXTENDED (uncles, cousins, nephews) ══════ -->
      <template v-if="extendedNodes.length">
        <div class="v-stem dashed" />
        <div class="gen-label muted">{{ $t('heir.group.unclesCousins') }}</div>
        <div class="fan-nodes" :class="{ single: extendedNodes.length === 1 }">
          <div class="fan-node" v-for="n in extendedNodes" :key="n.key">
            <TreeNodePill :node="n" :share="getPerPersonShare(n)" :blocked="isBlocked(n)" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import TreeNodePill from './TreeNodePill.vue'

const { t } = useI18n()

const props = defineProps({
  deceasedGender: { type: String, required: true },
  heirCounts: { type: Object, required: true },
  result: { type: Object, default: null },
})

// ─── Deceased pseudo-node ───
const deceasedNode = computed(() => ({
  category: 'deceased',
  label: t('calculator.deceased'),
  gender: props.deceasedGender,
  key: 'deceased',
}))

// ─── Expand a category count into individual nodes ───
// e.g. wife:2 → [{label:"Wife 1",...}, {label:"Wife 2",...}]
const MAX_EXPAND = 8

function expand(category, labelKey, gender) {
  const count = props.heirCounts[category] || 0
  if (count === 0) return []
  if (count > MAX_EXPAND) {
    return [{ category, label: `${t(labelKey)} ×${count}`, gender, key: category }]
  }
  return Array.from({ length: count }, (_, i) => ({
    category,
    label: count > 1 ? `${t(labelKey)} ${i + 1}` : t(labelKey),
    gender,
    key: `${category}-${i}`,
  }))
}

// ─── Generation groups ───

const grandparentNodes = computed(() => [
  ...expand('fathers_father', 'heir.fathersFather', 'male'),
  ...expand('paternal_grandmother', 'heir.paternalGrandmother', 'female'),
  ...expand('maternal_grandmother', 'heir.maternalGrandmother', 'female'),
])

const parentNodes = computed(() => [
  ...expand('father', 'heir.father', 'male'),
  ...expand('mother', 'heir.mother', 'female'),
])

const spouseNodes = computed(() => {
  if (props.deceasedGender === 'male') {
    return expand('wife', 'heir.wife', 'female')
  }
  return expand('husband', 'heir.husband', 'male')
})

const siblingNodes = computed(() => [
  ...expand('full_brother', 'heir.fullBrother', 'male'),
  ...expand('full_sister', 'heir.fullSister', 'female'),
  ...expand('paternal_half_brother', 'heir.paternalHalfBrother', 'male'),
  ...expand('paternal_half_sister', 'heir.paternalHalfSister', 'female'),
  ...expand('maternal_half_brother', 'heir.maternalHalfBrother', 'male'),
  ...expand('maternal_half_sister', 'heir.maternalHalfSister', 'female'),
])

const childNodes = computed(() => [
  ...expand('son', 'heir.son', 'male'),
  ...expand('daughter', 'heir.daughter', 'female'),
])

const grandchildNodes = computed(() => [
  ...expand('sons_son', 'heir.sonsSon', 'male'),
  ...expand('sons_daughter', 'heir.sonsDaughter', 'female'),
])

const extendedNodes = computed(() => [
  ...expand('full_brothers_son', 'heir.fullBrothersSon', 'male'),
  ...expand('paternal_half_brothers_son', 'heir.paternalHalfBrothersSon', 'male'),
  ...expand('full_paternal_uncle', 'heir.fullPaternalUncle', 'male'),
  ...expand('paternal_half_uncle', 'heir.paternalHalfUncle', 'male'),
  ...expand('full_cousin', 'heir.fullCousin', 'male'),
  ...expand('paternal_half_cousin', 'heir.paternalHalfCousin', 'male'),
])

const hasAnyHeirs = computed(() =>
  grandparentNodes.value.length > 0 ||
  parentNodes.value.length > 0 ||
  spouseNodes.value.length > 0 ||
  siblingNodes.value.length > 0 ||
  childNodes.value.length > 0 ||
  grandchildNodes.value.length > 0 ||
  extendedNodes.value.length > 0
)

// ─── Result lookups ───
function getPerPersonShare(node) {
  const shares = props.result?.shares
  if (!shares) return null
  const match = shares.find(s => s.heir_category === node.category)
  return match ? match.per_person_pct : null
}

function isBlocked(node) {
  const blocked = props.result?.blocked_heirs
  if (!blocked) return false
  return blocked.some(b => b.category === node.category)
}
</script>

<style scoped>
/* ─── Layout ─── */
.family-tree {
  text-align: center;
}

.tree-heading {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-gray-400, #9ca3af);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 16px;
}

.tree-canvas {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0 4px;
  direction: ltr; /* tree is a spatial diagram — keep LTR to preserve connector geometry */
}

/* ─── Empty state ─── */
.tree-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  gap: 16px;
}

.tree-hint {
  font-size: 12px;
  color: var(--color-gray-400, #9ca3af);
  margin: 0;
}

/* ─── Generation label ─── */
.gen-label {
  font-size: 9px;
  font-weight: 600;
  color: var(--color-gray-400, #9ca3af);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
}

.gen-label.muted {
  color: var(--color-gray-300, #d1d5db);
}

/* ─── Vertical stem between generations ─── */
.v-stem {
  width: 2px;
  height: 20px;
  background: #d1d5db;
  margin: 4px auto;
  flex-shrink: 0;
}

.v-stem.dashed {
  background: repeating-linear-gradient(
    to bottom,
    #d1d5db 0px, #d1d5db 4px,
    transparent 4px, transparent 8px
  );
}

/* ══════════════════════════════════════════════
   FAN-OUT CONNECTORS (T-shape tree lines)
   Classic CSS tree pattern using ::before/::after
   ══════════════════════════════════════════════ */

.fan-nodes {
  display: inline-flex;
  padding-top: 20px;
  position: relative;
  margin-bottom: 4px;
}

/* Vertical stem into the fan (from parent generation) */
.fan-nodes::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  height: 20px;
  background: #d1d5db;
  transform: translateX(-50%);
}

/* Single node: no horizontal rail needed */
.fan-nodes.single {
  padding-top: 0;
}
.fan-nodes.single::before {
  display: none;
}

/* Each node in the fan */
.fan-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 8px;
  position: relative;
}

/* Left half of horizontal rail */
.fan-node::before {
  content: '';
  position: absolute;
  top: -20px;
  right: 50%;
  width: 50%;
  height: 20px;
  border-top: 2px solid #d1d5db;
}

/* Right half of horizontal rail + vertical drop */
.fan-node::after {
  content: '';
  position: absolute;
  top: -20px;
  left: 50%;
  width: 50%;
  height: 20px;
  border-top: 2px solid #d1d5db;
  border-left: 2px solid #d1d5db;
}

/* First child: no left extension */
.fan-node:first-child::before {
  border: none;
}

/* First child: rounded top-left corner */
.fan-node:first-child::after {
  border-radius: 6px 0 0 0;
}

/* Last child: no right extension on ::after, add right border on ::before */
.fan-node:last-child::after {
  border-top: none;
}
.fan-node:last-child::before {
  border-right: 2px solid #d1d5db;
  border-radius: 0 6px 0 0;
}

/* Only child: just a vertical drop */
.fan-node:only-child::before,
.fan-node:only-child::after {
  border: none;
}
.fan-node:only-child::after {
  border-left: 2px solid #d1d5db;
  width: 0;
}

/* ══════════════════════════════════════════════
   CENTER ROW (Siblings + Deceased + Spouses)
   ══════════════════════════════════════════════ */

.center-row {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  margin: 8px 0;
  padding-top: 4px;
}

/* Horizontal connectors (sibling line, marriage line) */
.h-connector {
  width: 16px;
  height: 2px;
  background: #d1d5db;
  align-self: center;
  margin-top: -10px;
  flex-shrink: 0;
}

.marriage-connector {
  width: 20px;
  height: 2px;
  background: #dc2626;
  align-self: center;
  margin-top: -10px;
  flex-shrink: 0;
}

/* Side group (siblings or spouses cluster) */
.side-group {
  display: flex;
  gap: 4px;
  align-items: flex-start;
}

.side-member {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ══════════════════════════════════════════════
   RESPONSIVE
   ══════════════════════════════════════════════ */

@media (max-width: 640px) {
  .fan-node {
    padding: 0 4px;
  }

  .center-row {
    flex-wrap: wrap;
    gap: 4px;
  }
}
</style>
